import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import api from 'src/api/BlancoClient';
import {
  FollowListResponse,
  ResortHistoriesResponse,
  UserResortMyProfileResponse,
  UserResortProfileResponse
} from 'src/api/response/resort';
import { RootState } from 'src/features';
import { extractInvalidChars } from 'src/lib/message-validator';
import { ReportError } from 'src/models/report/ReportError';
import { ReportResortPayload } from 'src/models/report/ReportPayload';
import { FollowingUser } from 'src/models/resort/FollowingUser';
import { ResortUser } from 'src/models/resort/ResortUser';
import { UserResortMyProfile } from 'src/models/resort/UserResortMyProfile';
import { UserResortProfile } from 'src/models/resort/UserResortProfile';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType } from './utils/helper';
import { RefreshRequestState } from './utils/RefreshRequestState';
import { RequestState } from './utils/RequestState';

export type ResortErrorType =
  | 'already-following'
  | 'not-follow'
  | 'limit-of-follow'
  | 'not-exist'
  | 'loading-failed'
  | 'unstable-network'
  | 'unknown';

interface MyResortInfoPayload {
  profile: UserResortMyProfile;
  followList: FollowingUser[];
}

interface ResortUserInfoPayload {
  profile: UserResortProfile;
  followList: FollowingUser[];
}
export interface ResortState {
  myProfile?: RefreshRequestState<UserResortMyProfile, ResortErrorType>;
  followList?: RefreshRequestState<FollowingUser[], ResortErrorType>;
  histories?: RequestState<ResortUser[], ResortErrorType>;
  // NOTE: 自分自身のページと他人のページとでレスポンスの型が異なることから，displayedUserProfileの型が
  // 複雑になってしまっているため，もし次のリリースがあれば型の再設計を検討
  displayedUserProfile?: RequestState<UserResortProfile, ResortErrorType>; // 宣伝一覧を表示するプレイヤーの情報
  followStatus?: RequestState<Record<string, unknown>, ResortErrorType>;
  reportStatus?: RequestState<Record<string, unknown>, ReportError>;
  deleteStatus?: RequestState<Record<string, unknown>, ResortErrorType>;
}

const initialState: ResortState = {};

const slice = createSlice({
  name: 'resort',
  initialState,
  reducers: {
    getMyResortInfo: state => {
      state.myProfile = { state: 'pending' };
      state.followList = { state: 'pending' };
    },
    getMyResortInfoSucceeded: (state, { payload }: PayloadAction<MyResortInfoPayload>) => {
      state.myProfile = { state: 'ok', result: payload.profile };
      state.followList = { state: 'ok', result: payload.followList };
    },
    getMyResortInfoFailed: (state, { payload }: PayloadAction<ResortErrorType>) => {
      state.myProfile = { state: 'error', error: payload };
      state.followList = { state: 'error', error: payload };
    },
    updateMyResortInfo: state => {
      state.myProfile = {
        state: 'refresh',
        result: state.myProfile?.state === 'ok' ? state.myProfile.result : undefined
      };
      state.followList = {
        state: 'refresh',
        result: state.followList?.state === 'ok' ? state.followList.result : undefined
      };
    },
    getDeepLinkInfo: (state, _: PayloadAction<string>) => {
      state.displayedUserProfile = { state: 'pending' };
      state.followList = { state: 'pending' };
    },
    getDeepLinkInfoSucceeded: (state, { payload }: PayloadAction<ResortUserInfoPayload>) => {
      state.displayedUserProfile = { state: 'ok', result: payload.profile };
      state.followList = { state: 'ok', result: payload.followList };
    },
    getDeepLinkInfoFailed: (state, { payload }: PayloadAction<ResortErrorType>) => {
      state.displayedUserProfile = { state: 'error', error: payload };
      state.followList = { state: 'error', error: payload };
    },
    displayMyProfile: state => {
      if (state.myProfile?.state === 'ok') {
        const result: UserResortProfile = {
          ...state.myProfile.result,
          resortPlannerId: state.myProfile.result.resortPlannerId ?? '',
          resortPlannerGrade: state.myProfile.result.resortPlannerGrade ?? '',
          resortPlannerPromotionColor: state.myProfile.result.resortPlannerPromotionColor ?? 0,
          lastResortUpdatedAt: state.myProfile.result.lastResortUpdatedAt ?? 0
        };
        state.displayedUserProfile = { state: 'ok', result };
      } else {
        state.displayedUserProfile = { state: 'error', error: 'unknown' };
      }
    },
    getOtherProfile: (state, _: PayloadAction<string>) => {
      state.displayedUserProfile = { state: 'pending' };
    },
    getOtherProfileSucceeded: (state, { payload }: PayloadAction<UserResortProfile>) => {
      state.displayedUserProfile = { state: 'ok', result: payload };
    },
    getOtherProfileFailed: (state, { payload }: PayloadAction<ResortErrorType>) => {
      state.displayedUserProfile = { state: 'error', error: payload };
    },
    getHistories: state => {
      state.histories = { state: 'pending' };
    },
    getHistoriesSucceeded: (state, { payload }: PayloadAction<ResortUser[]>) => {
      state.histories = { state: 'ok', result: payload };
    },
    getHistoriesFailed: (state, { payload }: PayloadAction<ResortErrorType>) => {
      state.histories = { state: 'error', error: payload };
    },
    resetDeleteStatus: state => {
      state.deleteStatus = undefined;
    },
    deleteHistory: (state, _: PayloadAction<string>) => {
      state.deleteStatus = { state: 'pending' };
    },
    deleteHistorySucceeded: (state, { payload }: PayloadAction<string>) => {
      state.deleteStatus = { state: 'ok', result: {} };
      if (state.histories?.state === 'ok') {
        const updatedHistory: ResortUser[] = state.histories.result.filter(
          history => history.resortPlannerId !== payload
        );
        state.histories.result = updatedHistory;
      }
    },
    deleteHistoryFailed: (state, { payload }: PayloadAction<ResortErrorType>) => {
      state.deleteStatus = { state: 'error', error: payload };
    },
    resetFollowStatus: state => {
      state.followStatus = undefined;
    },
    followUser: (state, _: PayloadAction<FollowingUser>) => {
      state.followStatus = { state: 'pending' };
    },
    followUserSucceeded: (state, { payload }: PayloadAction<FollowingUser>) => {
      if (state.followList?.state === 'ok') {
        state.followList.result = [payload, ...state.followList.result];
      }
      state.followStatus = { state: 'ok', result: {} };
    },
    followUserFailed: (state, { payload }: PayloadAction<ResortErrorType>) => {
      state.followStatus = { state: 'error', error: payload };
    },
    unfollowUser: (state, _: PayloadAction<string>) => {
      state.followStatus = { state: 'pending' };
    },
    unfollowUserSucceeded: (state, { payload }: PayloadAction<string>) => {
      if (state.followList?.state === 'ok') {
        state.followList.result = state.followList.result.filter(
          user => user.resortPlannerId !== payload
        );
      }
      state.followStatus = { state: 'ok', result: {} };
    },
    unfollowUserFailed: (state, { payload }: PayloadAction<ResortErrorType>) => {
      state.followStatus = { state: 'error', error: payload };
    },
    resetReportStatus: state => {
      state.reportStatus = undefined;
    },
    report: (state, _: PayloadAction<ReportResortPayload>) => {
      state.reportStatus = { state: 'pending' };
    },
    reportSucceeded: (state, { payload }: PayloadAction<string>) => {
      state.reportStatus = { state: 'ok', result: {} };
      // フォロー削除成功済みなのでStoreのfollowListを更新する
      if (state.followList?.state === 'ok') {
        state.followList.result = state.followList.result.filter(
          user => user.resortPlannerId !== payload
        );
      }
    },
    reportFailed: (state, { payload }: PayloadAction<ReportError>) => {
      state.reportStatus = { state: 'error', error: payload };
    },
    reset: state => {
      state.myProfile = undefined;
      state.followList = undefined;
      state.histories = undefined;
      state.displayedUserProfile = undefined;
      state.followStatus = undefined;
      state.reportStatus = undefined;
      state.deleteStatus = undefined;
    }
  }
});

export default slice.reducer;

const {
  getMyResortInfo,
  updateMyResortInfo,
  displayMyProfile,
  getOtherProfile,
  getDeepLinkInfo,
  getHistories,
  resetDeleteStatus,
  deleteHistory,
  resetFollowStatus,
  followUser,
  unfollowUser,
  report,
  resetReportStatus,
  reset,
  ...internalActions
} = slice.actions;

export const actions = {
  getMyResortInfo,
  updateMyResortInfo,
  displayMyProfile,
  getOtherProfile,
  getDeepLinkInfo,
  getHistories,
  resetDeleteStatus,
  deleteHistory,
  resetFollowStatus,
  followUser,
  unfollowUser,
  report,
  resetReportStatus,
  reset
};

const {
  getMyResortInfoSucceeded,
  getMyResortInfoFailed,
  getOtherProfileSucceeded,
  getOtherProfileFailed,
  getDeepLinkInfoSucceeded,
  getDeepLinkInfoFailed,
  getHistoriesSucceeded,
  getHistoriesFailed,
  deleteHistorySucceeded,
  deleteHistoryFailed,
  followUserSucceeded,
  followUserFailed,
  unfollowUserSucceeded,
  unfollowUserFailed,
  reportSucceeded,
  reportFailed
} = internalActions;

// Saga

export function* ResortSaga() {
  yield takeLatest(getMyResortInfo.type, getMyResortInfoSaga);
  yield takeLatest(updateMyResortInfo.type, updateMyResortInfoSaga);
  yield takeLatest(getOtherProfile.type, getOtherProfileSaga);
  yield takeLatest(getDeepLinkInfo.type, getDeepLinkInfoSaga);
  yield takeLatest(getHistories.type, getHistoriesSaga);
  yield takeLatest(deleteHistory.type, deleteHistorySaga);
  yield takeLatest(followUser.type, followUserSaga);
  yield takeLatest(unfollowUser.type, unfollowUserSaga);
  yield takeLatest(report.type, reportSaga);
}

function* getMyResortInfoSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    const { language }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId || !language) {
      throw new Error();
    }
    const [myProfileRes, followListRes]: [
      UserResortMyProfileResponse,
      FollowListResponse
    ] = yield all([
      call(api.getMyUserResortProfile, baid, language),
      call(api.getFollowList, baid, selectedUserId)
    ]);
    const myProfile: UserResortMyProfile = {
      ...myProfileRes.userProfile,
      ...myProfileRes.resortPlannerProfile,
      constructions: myProfileRes.resorts ?? [],
      lastResortUpdatedAt: myProfileRes.lastResortUpdatedAt
    };

    yield put(
      getMyResortInfoSucceeded({
        profile: myProfile,
        followList: followListRes.resortPlanners
      })
    );
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getMyResortInfoFailed('unstable-network'));
    } else {
      yield put(getMyResortInfoFailed('loading-failed'));
    }
  }
}

function* updateMyResortInfoSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    const { language }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId || !language) {
      throw new Error();
    }
    const [myProfileRes, followListRes]: [
      UserResortMyProfileResponse,
      FollowListResponse
    ] = yield all([
      call(api.getMyUserResortProfile, baid, language),
      call(api.getFollowList, baid, selectedUserId)
    ]);
    const myProfile: UserResortMyProfile = {
      ...myProfileRes.userProfile,
      ...myProfileRes.resortPlannerProfile,
      constructions: myProfileRes.resorts ?? [],
      lastResortUpdatedAt: myProfileRes.lastResortUpdatedAt
    };

    yield put(
      getMyResortInfoSucceeded({
        profile: myProfile,
        followList: followListRes.resortPlanners
      })
    );
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getMyResortInfoFailed('unstable-network'));
    } else {
      yield put(getMyResortInfoFailed('loading-failed'));
    }
  }
}

function* getOtherProfileSaga(action: { type: typeof getOtherProfile.type; payload: string }) {
  try {
    const baid: string = yield call(getFreshToken);
    const { language }: UserState = yield select((state: RootState) => state.user);
    if (!language) {
      throw new Error();
    }
    const raid = action.payload;
    const res: UserResortProfileResponse = yield call(
      api.getOtherUserResortProfile,
      baid,
      raid,
      language
    );
    const profile: UserResortProfile = {
      ...res.userProfile,
      ...res.resortPlannerProfile,
      constructions: res.resorts,
      lastResortUpdatedAt: res.lastResortUpdatedAt
    };
    yield put(getOtherProfileSucceeded(profile));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getOtherProfileFailed('unstable-network'));
    } else {
      yield put(getOtherProfileFailed('loading-failed'));
    }
  }
}

function* getDeepLinkInfoSaga(action: { type: typeof getDeepLinkInfo.type; payload: string }) {
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    const { language }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId || !language) {
      throw new Error();
    }
    const raid = action.payload;
    const [followListRes, otherProfileRes]: [
      FollowListResponse,
      UserResortProfileResponse
    ] = yield all([
      call(api.getFollowList, baid, selectedUserId),
      call(api.getOtherUserResortProfile, baid, raid, language)
    ]);
    const userProfile: UserResortProfile = {
      ...otherProfileRes.userProfile,
      ...otherProfileRes.resortPlannerProfile,
      constructions: otherProfileRes.resorts,
      lastResortUpdatedAt: otherProfileRes.lastResortUpdatedAt
    };
    yield put(
      getDeepLinkInfoSucceeded({ followList: followListRes.resortPlanners, profile: userProfile })
    );
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getDeepLinkInfoFailed('unstable-network'));
    } else {
      yield put(getDeepLinkInfoFailed('loading-failed'));
    }
  }
}

function* getHistoriesSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId) {
      throw new Error();
    }
    const res: ResortHistoriesResponse = yield call(api.getResortHistories, baid, selectedUserId);
    yield put(getHistoriesSucceeded(res.resortPlanners));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getHistoriesFailed('unstable-network'));
    } else {
      yield put(getHistoriesFailed('loading-failed'));
    }
  }
}

function* deleteHistorySaga(action: { type: typeof deleteHistory.type; payload: string }) {
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId) {
      throw new Error();
    }
    yield call(api.deleteResortHistory, baid, selectedUserId, action.payload);
    yield put(deleteHistorySucceeded(action.payload));
  } catch (error) {
    console.error(error);
    switch (checkErrorType(error)) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          const resortError = getResortError(code);
          if (resortError === 'not-exist') {
            yield put(deleteHistorySucceeded(action.payload));
          } else {
            yield put(deleteHistoryFailed(resortError));
          }
        } else {
          yield put(deleteHistoryFailed('unknown'));
        }
        break;
      }
      case 'unstable-network':
        yield put(deleteHistoryFailed('unstable-network'));
        break;
      default:
        yield put(deleteHistoryFailed('unknown'));
    }
  }
}

function* followUserSaga(action: { type: typeof followUser.type; payload: FollowingUser }) {
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId) {
      throw new Error();
    }
    const { resortPlannerId } = action.payload;
    yield call(api.followUser, baid, selectedUserId, resortPlannerId);
    yield put(followUserSucceeded(action.payload));
  } catch (error) {
    console.error(error);
    switch (checkErrorType(error)) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          const resortError = getResortError(code);
          // 該当ユーザーをフォロー済みの場合は、成功と扱い、Reduxのフォローリストにユーザーを追加する
          if (resortError === 'already-following') {
            yield put(followUserSucceeded(action.payload));
          } else {
            yield put(followUserFailed(resortError));
          }
        } else {
          yield put(followUserFailed('unknown'));
        }
        break;
      }
      case 'unstable-network':
        yield put(followUserFailed('unstable-network'));
        break;
      default:
        yield put(followUserFailed('unknown'));
    }
  }
}

function* unfollowUserSaga(action: { type: typeof followUser.type; payload: string }) {
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId) {
      throw new Error();
    }
    yield call(api.unfollowUser, baid, selectedUserId, action.payload);
    yield put(unfollowUserSucceeded(action.payload));
  } catch (error) {
    console.error(error);
    switch (checkErrorType(error)) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          const resortError = getResortError(code);
          // 該当ユーザーを既にフォローしていないの場合は、成功と扱い、Reduxのフォローリストからユーザーを削除する
          if (resortError === 'not-follow') {
            yield put(unfollowUserSucceeded(action.payload));
          } else {
            yield put(unfollowUserFailed(resortError));
          }
        } else {
          yield put(unfollowUserFailed('unknown'));
        }
        break;
      }
      case 'unstable-network':
        yield put(unfollowUserFailed('unstable-network'));
        break;
      default:
        yield put(unfollowUserFailed('unknown'));
    }
  }
}

function* reportSaga(action: { type: typeof report.type; payload: ReportResortPayload }) {
  const { type, language, category, reason, ugc, isFollowing, raid } = action.payload;
  if (reason) {
    const invalidChars = extractInvalidChars(reason);
    if (invalidChars) {
      yield put(reportFailed({ type: 'invalid-char', char: invalidChars }));
      return;
    }
  }
  try {
    const baid: string = yield call(getFreshToken);
    const { selectedUserId }: UserState = yield select((state: RootState) => state.user);
    if (!selectedUserId) {
      throw new Error();
    }
    const { contentId, digest, createdAt } = ugc;
    yield call(
      api.reportSpam,
      type,
      category,
      reason,
      language,
      contentId,
      digest,
      createdAt,
      baid
    );
    if (isFollowing) {
      yield call(api.unfollowUser, baid, selectedUserId, raid);
    }
    yield put(reportSucceeded(raid));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          const resortError = getResortError(code);
          if (resortError === 'not-follow') {
            // 該当ユーザーを既にフォローしていないの場合は成功扱い
            yield put(reportSucceeded(action.payload.raid));
          } else {
            yield put(reportFailed({ type: 'unknown' }));
          }
        } else {
          yield put(reportFailed({ type: 'unknown' }));
        }
        break;
      }
      case 'unstable-network': {
        yield put(reportFailed({ type: 'unstable-network' }));
        break;
      }
      default:
        yield put(reportFailed({ type: 'unknown' }));
    }
  }
}

function getResortError(code?: string): ResortErrorType {
  switch (code) {
    case '1701':
      return 'limit-of-follow';
    case '1702':
      return 'already-following';
    case '1703':
      return 'not-follow';
    case '1704':
      return 'not-exist';
    default:
      return 'unknown';
  }
}
